/**
 * The Tools group's page structure, derived ENTIRELY from the registry response.
 *
 * `tool.group` is free text the backend chooses ("Phase B", "CRM identity",
 * "Affinity cut", …). One group string = one on-page `<section>` = one sidebar
 * link, so a group added in a backend PR grows the page and the nav with zero
 * frontend change. This module is the single home of that derivation — AdminPage
 * (which builds the sidebar) and ToolsAdminGroup (which renders the sections)
 * both import it, so the two can never disagree about a section id.
 */
import type { MaintenanceTool } from '../../../hooks/useAdmin';

/** The audit-log section, which is not registry-derived and always comes last. */
export const RECENT_RUNS_SECTION_ID = 'recent-runs';

export interface ToolGroupSection {
  /** The registry's own words — rendered as the section heading and nav label. */
  name: string;
  /** The element id and URL hash for this section. */
  slug: string;
  tools: MaintenanceTool[];
}

/**
 * "Phase B" → `phase-b`, "CRM identity" → `crm-identity`.
 *
 * Lower-case, every run of non-alphanumerics collapsed to one hyphen, no
 * leading or trailing hyphen. A name with no alphanumerics at all would slug to
 * the empty string, which is not a usable element id, so it falls back to
 * `group` and the collision rule in `toolGroupSections` takes it from there.
 */
export function slugifyGroup(name: string): string {
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return slug || 'group';
}

/**
 * One section per distinct `tool.group`, ordered by FIRST APPEARANCE in the
 * response — the registry's declaration order is the page's order, so the
 * backend controls it.
 *
 * Two different group names can in principle slug to the same id ("Phase B" and
 * "phase—b"), as can a group literally named "Recent runs". That would put two
 * elements on the page under one id, which breaks both the hash link and the
 * scroll-spy, so the later group is dropped rather than rendered: first wins,
 * and the fixed `recent-runs` section is claimed before any of them.
 */
export function toolGroupSections(tools: MaintenanceTool[]): ToolGroupSection[] {
  const sections: ToolGroupSection[] = [];
  const byName = new Map<string, ToolGroupSection>();
  const takenSlugs = new Set<string>([RECENT_RUNS_SECTION_ID]);

  for (const tool of tools) {
    const existing = byName.get(tool.group);
    if (existing) {
      existing.tools.push(tool);
      continue;
    }
    const slug = slugifyGroup(tool.group);
    if (takenSlugs.has(slug)) continue;
    takenSlugs.add(slug);
    const section: ToolGroupSection = { name: tool.group, slug, tools: [tool] };
    byName.set(tool.group, section);
    sections.push(section);
  }
  return sections;
}
